class IvContactWidget extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-iv-contact-trigger]');
    this.panel = this.querySelector('[data-iv-contact-panel]');
    this.backdrop = this.querySelector('[data-iv-contact-backdrop]');
    this.closeButton = this.querySelector('[data-iv-contact-close]');
    this.lastFocused = null;

    if (!this.trigger || !this.panel || !this.backdrop) return;

    this.trigger.addEventListener('click', () => this.toggle());
    this.closeButton?.addEventListener('click', () => this.close());
    this.backdrop.addEventListener('click', () => this.close());

    this.panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      this._trapFocus(event);
    });

    this._setupFooterAvoidance();
  }

  // Keeps the (always position: fixed) trigger from covering the footer.
  // Computed straight from scroll math in viewport-relative terms, rather
  // than switching to position: absolute — that resolves against the
  // viewport-sized initial containing block anchored near the document's
  // *top*, not the true scrollable bottom, so it isn't usable here.
  _setupFooterAvoidance() {
    const baseOffsetPx = 16; // matches the CSS `bottom: 1rem` default
    const dockedGapPx = 72; // matches the desired 4.5rem clearance above the page's true end
    let ticking = false;

    const update = () => {
      ticking = false;
      const scrollBottom = window.scrollY + window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;
      const distanceFromPageEnd = Math.max(0, pageHeight - scrollBottom);
      const dockedOffsetPx = dockedGapPx - distanceFromPageEnd;
      const offsetPx = Math.max(baseOffsetPx, dockedOffsetPx);
      this.trigger.style.bottom = `${offsetPx}px`;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();
  }

  isOpen() {
    return this.panel.getAttribute('data-state') === 'open';
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    this.lastFocused = document.activeElement;

    this.panel.removeAttribute('inert');
    this.panel.setAttribute('data-state', 'open');
    this.backdrop.setAttribute('data-state', 'open');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('iv-contact-widget-open');

    (this.closeButton || this.panel).focus();
  }

  close() {
    this.panel.setAttribute('data-state', 'closed');
    this.backdrop.setAttribute('data-state', 'closed');
    this.panel.setAttribute('inert', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('iv-contact-widget-open');

    if (this.lastFocused && document.contains(this.lastFocused)) {
      this.lastFocused.focus();
    } else {
      this.trigger.focus();
    }
  }

  _trapFocus(event) {
    if (event.key !== 'Tab') return;

    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('iv-contact-widget', IvContactWidget);